import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { phoneRule } from '@/lib/phoneCountryRule';
import { AccountDetailsFields, validateAccountForm, accountPayload } from '@/components/AccountDetailsFields';

const API = process.env.REACT_APP_BACKEND_URL;

/* details_gate_27oct2026 (CEO, replaces the September ruling): full-page
   gate for ANY account with missing details, every role. No 'Not now'.
   The only other option is Log out. Server refuses every API action too. */

const FIELD_NAMES = {
  first_name: 'First name', last_name: 'Surname',
  date_of_birth: 'Date of birth', phone: 'Mobile number',
  company: 'Company / Salon name', address: 'Street address',
  city: 'Town or city', postcode: 'Postcode',
  state: 'State / Country', country: 'Country',
};

const dobToInput = (v) => {
  const m = String(v || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return /^\d{4}-\d{2}-\d{2}/.test(String(v || '')) ? String(v).slice(0, 10) : '';
};

export default function CompleteProfilePage() {
  const { user, token, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState(() => ({
    first_name: user?.first_name || (user?.name || '').split(' ')[0] || '',
    last_name: user?.last_name || (user?.name || '').split(' ').slice(1).join(' ') || '',
    date_of_birth: dobToInput(user?.date_of_birth),
    phone: user?.phone || user?.phone_mobile || '',
    address: user?.address || '',
    city: user?.city || user?.area || '',
    postcode: user?.postcode || '',
    state: user?.state || '',
    country: user?.country || ((user?.region || 'uk').toLowerCase() === 'uk' ? 'United Kingdom' : ''),
    company: user?.salon_name || user?.company_name || '',
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const phoneStamp = phoneRule(values.phone);
  const region = phoneStamp ? phoneStamp.region : (user?.region || 'uk').toLowerCase();
  const companyRequired = ['salon', 'salon_owner', 'distributor', 'salon_business'].includes(user?.role);

  const currentErrors = useMemo(() => {
    const errs = validateAccountForm(values, { region, companyRequired, requireEmail: false });
    if (!String(values.country || '').trim()) errs.country = 'Enter the country.';
    return errs;
  }, [values, region, companyRequired]);
  const ready = Object.keys(currentErrors).length === 0;

  const doLogout = () => { logout(); navigate('/login'); };

  const submit = async () => {
    if (!ready) {
      setErrors(currentErrors);
      const names = Object.keys(currentErrors).map((k) => FIELD_NAMES[k] || k).join(', ');
      toast.error(`Still needed: ${names}`);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/auth/complete-profile`,
        { ...accountPayload(values), salon_name: values.company, country: values.country, region },
        { headers: { Authorization: `Bearer ${token}` } });
      if (updateUser && res.data?.user) updateUser(res.data.user);
      toast.success('Details saved — thank you.');
      navigate('/dashboard');
      window.location.reload();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.missing) {
        const map = {};
        d.missing.forEach((m) => { map[m.field === 'company' ? 'company' : m.field] = m.message; });
        setErrors(map);
        toast.error('Please complete the highlighted boxes.');
      } else {
        toast.error(typeof d === 'string' ? d : 'Could not save details');
      }
    }
    setSaving(false);
  };

  const inputCls = (err) => `w-full h-10 px-3 rounded-lg border text-sm bg-white ${err ? 'border-red-500' : 'border-[#E8DDD5]'}`;

  return (
    <div className="min-h-screen" style={{ background: '#F8F4F1' }} data-testid="details-gate-page">
      <div className="px-5 py-4 flex items-center justify-between" style={{ background: '#291525' }}>
        <div style={{ fontFamily: "'Bodoni Moda', Georgia, serif", color: '#F8F4F1', letterSpacing: '3px', fontSize: 16 }}>
          REVITALISE KERATIN
        </div>
        <button type="button" onClick={doLogout} data-testid="details-gate-logout-top"
          className="flex items-center gap-2 text-sm rounded-full px-4 py-2"
          style={{ color: '#D9BE8E', border: '1px solid #D9BE8E', background: 'transparent' }}>
          <LogOut size={14} /> Log out
        </button>
      </div>
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <p className="text-[11px] uppercase mb-2" style={{ color: '#7E5F2E', letterSpacing: '3px' }}>Before you carry on</p>
        <h1 className="text-3xl sm:text-4xl mb-2" style={{ fontFamily: "'Bodoni Moda', Georgia, serif", color: '#2B1F27' }}>
          Complete your <em style={{ color: '#7A3E22' }}>details.</em>
        </h1>
        <p className="text-sm text-[#64525D] mb-5">
          Every account needs full details before anything else can be done on the platform.
          The platform is for professionals aged 18 and over. Anything already on file is pre-filled.
        </p>
        <div className="bg-white border border-[#E8DDD5] rounded-2xl p-4 sm:p-6">
          <AccountDetailsFields values={values} onChange={setValues} errors={errors}
            region={region} showCompany={companyRequired} showEmail={false} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-[#64525D]">Email</label>
              <input className={inputCls()} value={user?.email || ''} disabled data-testid="acct-email-fixed"
                style={{ background: '#F6F0EA', color: '#64525D' }} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-[#64525D]">Country</label>
              <input className={inputCls(errors.country)} value={values.country || ''}
                onChange={(e) => setValues({ ...values, country: e.target.value })} data-testid="acct-country" />
              {errors.country && <p className="text-[11px] text-red-600" data-testid="field-error-country">{errors.country}</p>}
            </div>
          </div>
          <button type="button" onClick={submit} disabled={saving} data-testid="details-gate-save"
            className="w-full mt-5 rounded-full text-sm uppercase tracking-widest font-semibold transition-colors"
            style={{
              minHeight: 52,
              background: ready ? '#291525' : '#CDbfc9'.toUpperCase(),
              color: ready ? '#D9BE8E' : '#FFFFFF',
              cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}>
            {saving ? 'Saving…' : 'Save and continue'}
          </button>
          <button type="button" onClick={doLogout} data-testid="details-gate-logout"
            className="w-full mt-3 rounded-full text-sm font-semibold"
            style={{ minHeight: 48, background: '#FFFFFF', border: '1px solid #7A3E22', color: '#7A3E22' }}>
            <span className="inline-flex items-center gap-2"><LogOut size={15} /> Log out</span>
          </button>
        </div>
      </div>
    </div>
  );
}
